import "./DocCheckListTaxOrg.scss";
import { useMediaQuery } from "@mantine/hooks";
import CheckListMobileView from "./CheckListMobileView/CheckListMobileView";
import CheckListDesktopView from "./CheckListDesktopView/CheckListDesktopView";

interface DocCheckListTaxOrgProps {
  docsToUpload: Record<string, number>;
  responses: any;
  styles: {
    padding: string;
    border: string;
  };
  showTitle?: boolean;
}

const documentDescriptions = {
  "W-2": "Wage statement - form W-2s from this year (one per employer)",
  "1099-R": "Retirement Income",
  "1099-INT": "Interest Income",
  "Government-issued ID":
    "e.g. driver's license, Government-issued ID, Passport",
  "1099-NEC": "Nonemployee compensation",
  "1099-G": "Unemployment compensation",
};

const titleMapping = {
  "spouse-W-2": "W-2",
  "spouse-1099-NEC": "1099-NEC",
  "spouse-1099-G": "1099-G",
  id: "Government-issued ID",
  "spouse-id": "Government-issued ID",
};

const DocCheckListTaxOrg: React.FC<DocCheckListTaxOrgProps> = ({
  docsToUpload,
  responses,
  styles,
  showTitle,
}: DocCheckListTaxOrgProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const totalDocuments =
    Object.values(docsToUpload)?.reduce((acc, curr) => acc + curr, 0) || 0;

  // Create a local copy and add 'id' if not present
  const localDocsToUpload = { ...docsToUpload };
  if (!localDocsToUpload.hasOwnProperty("id")) {
    localDocsToUpload["id"] = 1;
  }

  const documentOrder = [
    "Government-issued ID",
    "1099-G",
    "1099-NEC",
    "W-2",
    "1098",
    "1098-E",
    "1098-T",
    "1098",
    "1099-R",
    "1099-INT",
    "1099-B",
    "1095-A",
  ];

  const sortedDocs: [string, number][] = Object.entries(localDocsToUpload).sort(
    ([a], [b]) => {
      const isSpouseA = a.startsWith("spouse-");
      const isSpouseB = b.startsWith("spouse-");

      if (isSpouseA !== isSpouseB) {
        return isSpouseA ? 1 : -1;
      }

      const baseDocA = isSpouseA ? a.slice(7) : a;
      const baseDocB = isSpouseB ? b.slice(7) : b;

      const titleA = titleMapping[a as keyof typeof titleMapping] ?? baseDocA;
      const titleB = titleMapping[b as keyof typeof titleMapping] ?? baseDocB;

      return documentOrder.indexOf(titleA) - documentOrder.indexOf(titleB);
    }
  );

  // Update docsArray to include the potentially added 'id'
  const docsArray = sortedDocs.map(([docType, count]) => ({
    [docType]: count,
  }));

  const allDocsToUpload = [...docsArray];

  const documents = allDocsToUpload.map((doc) => {
    const [docType, count] = Object.entries(doc)[0];
    const title = titleMapping[docType as keyof typeof titleMapping] ?? docType;

    return {
      type: docType,
      description:
        documentDescriptions[title as keyof typeof documentDescriptions] ?? "",
      count: count,
      title: titleMapping[docType as keyof typeof titleMapping] ?? docType,
    };
  });

  const hasSpouseDocs = documents.some((doc) => doc.type.includes("spouse"));

  return (
    <div
      className="document-upload-container"
      style={{
        padding: !isMobile ? (styles.padding ?? "2.5rem") : undefined,
        border: !isMobile ? (styles.border ?? "1px solid #e0e0e0") : undefined,
        boxShadow: "none",
      }}
    >
      {isMobile ? (
        <CheckListMobileView
          documents={documents}
          showTitle={showTitle}
          responses={responses}
          hasSpouseDocs={hasSpouseDocs}
        />
      ) : (
        <CheckListDesktopView
          documents={documents}
          totalDocuments={totalDocuments}
          showTitle={showTitle}
          responses={responses}
          hasSpouseDocs={hasSpouseDocs}
        />
      )}
    </div>
  );
};

export default DocCheckListTaxOrg;
